import type { NewsItem as INewsItem } from '@bulknutrients/bulk-shared-lib/interfaces/Sanity';
import {
  copyLink,
  formatDateAsSentence,
} from '@bulknutrients/bulk-shared-lib/utils';
import { ErrorAlert } from '@components/Alerts/ErrorAlert';
import { Icons } from '@components/Icons/Icons';
import { PortableTextBlockRenderer } from '@components/PortableText/PortableText';
import { useState } from 'react';

function CopyLinkButton({ url }: { url: string }) {
  const [isCopied, setIsCopied] = useState(false);
  const [failCopy, setFailCopy] = useState(false);

  const onCopied = () => {
    // Show copied message if clipboard returns successfully
    setIsCopied(true);
    setTimeout(() => setIsCopied(false), 1500);
  };

  const onFailed = () => {
    // Show copy fail message if clipboard writing fails, i.e. permissions blocked
    setFailCopy(true);
    setTimeout(() => setFailCopy(false), 2000);
  };

  return (
    <>
      <ErrorAlert show={failCopy} setShow={setFailCopy}>
        <p>No copy access in this browser</p>
      </ErrorAlert>
      <button
        className=""
        type="button"
        onClick={() => copyLink(url, onCopied, onFailed)}
      >
        {/* If failure to copy, overwrite and show cant copy message */}
        {isCopied ? (
          <span
            className="animate-[copy-confirmation_forwards] opacity-0 delay-200
        duration-100"
          >
            <Icons.CheckMark size="1rem" />
          </span>
        ) : (
          <span className="animate-copy-indicator duration-200">
            <Icons.Link size="1rem" />
          </span>
        )}
      </button>
    </>
  );
}

export function NewsItem({
  item,
  baseUrl,
}: {
  item: INewsItem;
  baseUrl: string;
}) {
  const date = new Date(item.datetime);
  const anchor = `${(`0${date.getMonth() + 1}`).slice(-2)}-${(`0${date.getDate()}`).slice(-2)}-${(`0${date.getHours()}`).slice(-2)}-${(`0${date.getMinutes()}`).slice(-2)}`;

  return (
    <div className="card relative mx-0 my-5 bg-grey-light px-5 pb-2.5 pt-5 dark:bg-grey-darkest rounded-2xl">
      {item.pinned && (
        <Icons.Pin
          style={{ position: 'absolute', top: '10px', right: '10px' }}
        />
      )}
      <h3 className="flex items-center gap-2 font-bold" id={anchor}>
        {item.title} <CopyLinkButton url={`${baseUrl}#${anchor}`} />
      </h3>
      <span className="italic">{formatDateAsSentence(date)}</span>
      <PortableTextBlockRenderer content={item.description} />
    </div>
  );
}
